<template>
  <div class="row glovo-laas-integration-form">
    <div v-if="isCreateMode" class="col-md-12">
      <div class="glovo-laas-integration-form__setup" role="status">
        <p class="glovo-laas-integration-form__setup-title">{{ $t('gestiune.integrationsGlovoLaasSetupTitle') }}</p>
        <ol class="glovo-laas-integration-form__setup-steps">
          <li>
            {{ $t('gestiune.integrationsGlovoLaasSetupStep1') }}
            <a
              href="https://laaspartners.glovoapp.com/orders"
              target="_blank"
              rel="noopener noreferrer"
            >https://laaspartners.glovoapp.com/orders</a>
          </li>
          <li>
            {{ $t('gestiune.integrationsGlovoLaasSetupStep2') }}
            <a
              href="https://portal.integrations.cloud.onlineservice.io/partner"
              target="_blank"
              rel="noopener noreferrer"
            >https://portal.integrations.cloud.onlineservice.io/partner</a>
          </li>
        </ol>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('location_id') }">
        <label>{{ fieldLabel('location_id') }}<span v-if="isRequired('location_id')" class="text-danger"> *</span></label>
        <select
          v-model="formData.location_id"
          class="form-control"
          :disabled="disabled"
        >
          <option :value="null">{{ $t('general.select') }}</option>
          <option
            v-for="loc in locations"
            :key="'glovo-laas-loc-' + String(loc._integrationLocationId)"
            :value="loc._integrationLocationId"
            :disabled="isLocationOccupied(loc._integrationLocationId)"
            :class="{ 'integration-location-option--occupied': isLocationOccupied(loc._integrationLocationId) }"
          >
            {{ locationOptionLabel(loc) }}
          </option>
        </select>
        <span v-if="showFieldError('location_id')" class="text-danger help-block">{{ fieldErrorMessage('location_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('api_url') }">
        <label>{{ fieldLabel('api_url') }}<span v-if="isRequired('api_url')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.api_url"
          :disabled="disabled"
        >
        <span v-if="showFieldError('api_url')" class="text-danger help-block">{{ fieldErrorMessage('api_url') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('pos_implementation_name') }">
        <label>{{ fieldLabel('pos_implementation_name') }}<span v-if="isRequired('pos_implementation_name')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.pos_implementation_name"
          :disabled="disabled"
        >
        <span v-if="showFieldError('pos_implementation_name')" class="text-danger help-block">{{ fieldErrorMessage('pos_implementation_name') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('brand_name') }">
        <label>{{ fieldLabel('brand_name') }}<span v-if="isRequired('brand_name')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.brand_name"
          :disabled="disabled"
        >
        <span v-if="showFieldError('brand_name')" class="text-danger help-block">{{ fieldErrorMessage('brand_name') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('laas_client_id') }">
        <label>{{ fieldLabel('laas_client_id') }}<span v-if="isRequired('laas_client_id')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.laas_client_id"
          :disabled="disabled"
        >
        <span v-if="showFieldError('laas_client_id')" class="text-danger help-block">{{ fieldErrorMessage('laas_client_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('laas_client_secret') }">
        <label>{{ fieldLabel('laas_client_secret') }}<span v-if="isRequired('laas_client_secret')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.laas_client_secret"
          :disabled="disabled"
        >
        <span v-if="showFieldError('laas_client_secret')" class="text-danger help-block">{{ fieldErrorMessage('laas_client_secret') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="glovo-laas-integration-form__auto-send-row">
        <div class="glovo-laas-integration-form__auto-send-status">
          <div class="form-group" :class="{ 'has-error': showFieldError('auto_send_at_status') }">
            <label>{{ fieldLabel('auto_send_at_status') }}</label>
            <select
              v-model="formData.auto_send_at_status"
              class="form-control"
              :disabled="disabled"
            >
              <option
                v-for="opt in autoSendStatusOptions"
                :key="'glovo-laas-status-' + opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <span v-if="showFieldError('auto_send_at_status')" class="text-danger help-block">{{ fieldErrorMessage('auto_send_at_status') }}</span>
          </div>
        </div>
        <div class="glovo-laas-integration-form__auto-send-time">
          <div class="form-group" :class="{ 'has-error': showFieldError('auto_send_time') }">
            <label>{{ fieldLabel('auto_send_time') }}</label>
            <select
              v-model="formData.auto_send_time"
              class="form-control"
              :disabled="disabled || isAutoSendManual"
            >
              <option
                v-for="mins in autoSendTimeOptions"
                :key="'glovo-laas-time-' + mins"
                :value="mins"
              >
                {{ mins }}
              </option>
            </select>
            <span v-if="showFieldError('auto_send_time')" class="text-danger help-block">{{ fieldErrorMessage('auto_send_time') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  WOLT_DRIVE_AUTO_SEND_STATUS_VALUES,
  WOLT_DRIVE_AUTO_SEND_TIME_VALUES
} from 'src/components/Dashboard/Views/Integrations/WoltDriveIntegrationForm.vue'
import {
  getIntegrationFieldErrorMessage,
  hasIntegrationFieldError
} from 'src/components/Dashboard/Views/Integrations/integrationFormFieldHelpers.js'

export const DEFAULT_GLOVO_LAAS_API_URL = 'https://api.glovoapp.com'
export const DEFAULT_GLOVO_LAAS_POS_IMPLEMENTATION_NAME = 'taptasty'

export const GLOVO_LAAS_AUTO_SEND_STATUS_VALUES = WOLT_DRIVE_AUTO_SEND_STATUS_VALUES
export const GLOVO_LAAS_AUTO_SEND_TIME_VALUES = WOLT_DRIVE_AUTO_SEND_TIME_VALUES

export default {
  name: 'GlovoLaasIntegrationForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    locations: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fieldLabelFn: {
      type: Function,
      default: null
    },
    requiredFields: {
      type: Array,
      default: () => []
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    showFieldValidation: {
      type: Boolean,
      default: false
    },
    occupiedLocationIds: {
      type: Array,
      default: () => []
    },
    isCreateMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    autoSendStatusOptions () {
      return GLOVO_LAAS_AUTO_SEND_STATUS_VALUES.map(value => ({
        value,
        label: this.autoSendStatusLabel(value)
      }))
    },
    autoSendTimeOptions () {
      return GLOVO_LAAS_AUTO_SEND_TIME_VALUES.slice()
    },
    isAutoSendManual () {
      return String(this.formData.auto_send_at_status || '').toUpperCase() === 'MANUAL'
    }
  },
  created () {
    this.ensureDefaults()
  },
  methods: {
    fieldLabel (field) {
      if (typeof this.fieldLabelFn === 'function') return this.fieldLabelFn(field)
      return field
    },
    isRequired (field) {
      return (this.requiredFields || []).includes(field)
    },
    hasFieldError (field) {
      return Boolean(this.validationErrors && this.validationErrors[field])
    },
    showFieldError (field) {
      return hasIntegrationFieldError({
        field,
        validationErrors: this.validationErrors,
        showFieldValidation: this.showFieldValidation,
        requiredFields: this.requiredFields,
        formData: this.formData
      })
    },
    fieldErrorMessage (field) {
      return getIntegrationFieldErrorMessage({
        field,
        validationErrors: this.validationErrors,
        showFieldValidation: this.showFieldValidation,
        requiredFields: this.requiredFields,
        formData: this.formData,
        requiredMessage: this.$t('validations.requiredField')
      })
    },
    isLocationOccupied (locationId) {
      if (locationId == null || locationId === '') return false
      return (this.occupiedLocationIds || []).map(String).includes(String(locationId))
    },
    locationOptionLabel (loc) {
      const name = loc && loc.name ? loc.name : ''
      if (this.isLocationOccupied(loc._integrationLocationId)) {
        return `${name} (${this.$t('gestiune.integrationsLocationOccupied')})`
      }
      return name
    },
    autoSendStatusLabel (value) {
      const key = `gestiune.woltDriveAutoSendStatus_${String(value || '').toLowerCase()}`
      const translated = this.$t(key)
      return translated !== key ? translated : String(value || '')
    },
    ensureDefaults () {
      const url = this.formData.api_url
      if (url === undefined || url === null || String(url).trim() === '') {
        this.$set(this.formData, 'api_url', DEFAULT_GLOVO_LAAS_API_URL)
      }

      const posName = this.formData.pos_implementation_name
      if (posName === undefined || posName === null || String(posName).trim() === '') {
        this.$set(this.formData, 'pos_implementation_name', DEFAULT_GLOVO_LAAS_POS_IMPLEMENTATION_NAME)
      }

      let status = this.formData.auto_send_at_status
      status = status === undefined || status === null || String(status).trim() === ''
        ? ''
        : String(status).trim().toUpperCase()
      if (!status || !GLOVO_LAAS_AUTO_SEND_STATUS_VALUES.includes(status)) {
        status = 'MANUAL'
      }
      this.$set(this.formData, 'auto_send_at_status', status)

      let time = this.formData.auto_send_time
      time = time === null || time === undefined || String(time).trim() === ''
        ? ''
        : String(parseInt(String(time).trim(), 10))
      if (!time || !GLOVO_LAAS_AUTO_SEND_TIME_VALUES.includes(time)) {
        time = '30'
      }
      this.$set(this.formData, 'auto_send_time', time)
    }
  }
}
</script>

<style scoped>
.glovo-laas-integration-form__setup {
  margin-bottom: 18px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #eef0fb;
  border: 1px solid #c5cae9;
  color: #25396f;
}
.glovo-laas-integration-form__setup-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
}
.glovo-laas-integration-form__setup-steps {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.5;
}
.glovo-laas-integration-form__setup-steps li + li {
  margin-top: 6px;
}
.glovo-laas-integration-form__setup-steps a {
  word-break: break-all;
}
.glovo-laas-integration-form__auto-send-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}
.glovo-laas-integration-form__auto-send-status {
  flex: 2 1 0;
  min-width: 0;
}
.glovo-laas-integration-form__auto-send-time {
  flex: 1 0 150px;
  min-width: 150px;
  max-width: 200px;
}
.glovo-laas-integration-form__auto-send-time label {
  white-space: nowrap;
}
.glovo-laas-integration-form__auto-send-time .form-control {
  width: 100%;
}
.glovo-laas-integration-form .has-error .form-control {
  border-color: #dc3545;
}
.glovo-laas-integration-form .help-block {
  margin-top: 4px;
  margin-bottom: 0;
}
.glovo-laas-integration-form option.integration-location-option--occupied {
  color: #9e9e9e;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/GlovoLaasIntegrationForm.vue